// src/components/CodeDisplay.tsx
import React, { useState, useEffect } from 'react';
import { CodeBlock } from '../types/chat';
import { analyticsApi } from '../services/api';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { trackCodeExecuted } from '../utils/analytics';

interface Props {
  codeBlocks: CodeBlock[];
  currentMessageId?: string;
  displayMode?: 'execution' | 'preview';
  isStreaming?: boolean;
  sessionId?: string;
}

interface ExecutionResult {
  success: boolean;
  output?: string;
  error?: string;
  execution_time?: string;
  memory_used?: string;
  status?: string;
  language?: string;
  generated_files?: Array<{
    filename: string;
    blob_path: string;
    size: number;
    download_url: string;
    image_base64?: string;
    image_mime?: string;
  }>;
}

const CodeDisplay: React.FC<Props> = ({ codeBlocks, currentMessageId, displayMode = 'execution', isStreaming = false, sessionId }) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [executionResults, setExecutionResults] = useState<{ [key: number]: ExecutionResult }>({});
  const [executingIndex, setExecutingIndex] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userInputs, setUserInputs] = useState<{ [key: number]: string }>({});
  const [showInputModal, setShowInputModal] = useState<number | null>(null);
  const [modalInput, setModalInput] = useState<string>('');
  const [multipleInputs, setMultipleInputs] = useState<string[]>([]);
  const codeBlockRefs = React.useRef<{ [key: number]: HTMLDivElement | null }>({});
  const executionResultRefs = React.useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Filter out TEXT/OUTPUT blocks - they're just informational, not actual executable code
  // Only show blocks with executable languages AND blocks that look like actual code
  const filteredCodeBlocks = codeBlocks.filter(block => {
    const lang = block.language.toLowerCase();
    const code = block.code.trim();

    // Remove explicit text/output blocks
    if (lang === 'text' || lang === 'output' || lang === '') {
      return false;
    }

    // Check for output-only patterns first (before code detection)
    const outputPatterns = [
      /^(Fibonacci Series|Formatted output|Output|Result|Example output):/im,  // Output descriptions
      /^(Term|Item|Line|Row|Entry)\s+\d+:/im,  // Formatted output like "Term 1:", "Item 2:"
      /^[\d\s\[\],]+$/,  // Just numbers, brackets, commas, spaces (like "[1, 2, 3]")
    ];

    const isOutputOnly = outputPatterns.some(pattern => pattern.test(code));
    if (isOutputOnly) {
      console.log('[CodeDisplay] Filtering out output-only block:', code.substring(0, 50));
      return false;
    }

    // If it's just numbers and spaces (output), filter it out
    const isJustNumbersAndSpaces = /^[\d\s.]+$/.test(code);
    if (isJustNumbersAndSpaces) {
      console.log('[CodeDisplay] Filtering out output-only block:', code.substring(0, 50));
      return false;
    }

    // Check if block has actual code patterns (functions, imports, control flow)
    // NOTE: Removed brackets/parentheses alone - they can appear in output too
    const codePatterns = [
      /\bdef\s+\w+/,  // Python function
      /\bfunction\s+\w+/,  // JS function
      /\bclass\s+\w+/,  // Class definition
      /\bimport\s+/,  // Import statement
      /\bfrom\s+\w+\s+import/,  // Python import
      /\bfor\s+\w+\s+in\s+/,  // For loop
      /\bwhile\s+/,  // While loop
      /\bif\s+.*:/,  // If statement (Python)
      /\bif\s*\(/,  // If statement (JS/Java/C/C++)
      /\bprint\s*\(/,  // Print statement (actual code)
      /\bconsole\./,  // Console methods
      /\breturn\s+/,  // Return statement
      /\bdef\b|\bclass\b/,  // Python keywords
      /^\s*\w+\s*=\s*[^=]/m,  // Variable assignment (x = 5), not comparison (==)
      /\b(var|let|const|int|float|double|string|void|public|private|protected)\b/,  // Variable declarations
      /\bSystem\.out\./,  // Java print
      /\bfmt\.\w+/,  // Go fmt package
      /\bprintf\s*\(/,  // C/C++ printf
      /\b(println!|fn\s+\w+)/,  // Rust patterns
      /\bmain\s*\(/,  // Main function (C/Java/Go)
      /\bpackage\s+\w+/,  // Go/Java package
      /\b#include\s*</,  // C/C++ include
    ];

    const hasCodePattern = codePatterns.some(pattern => pattern.test(code));

    // If it's a known executable language, check if it has code patterns
    const executableLanguages = ['python', 'javascript', 'js', 'java', 'cpp', 'c', 'py', 'go', 'golang', 'rust', 'ruby', 'php'];
    if (executableLanguages.includes(lang)) {
      // Even if it's marked as Python, if it doesn't have ANY code patterns, it's just output
      if (!hasCodePattern) {
        console.log('[CodeDisplay] Filtering out non-code block despite language tag:', code.substring(0, 50));
        return false;
      }
      return true;
    }

    // For other/unknown languages, check if it looks like code
    return hasCodePattern;
  });

  // Clear execution results when code blocks change (new code generated)
  useEffect(() => {
    console.log('[CodeDisplay] Code blocks changed, clearing execution results');
    setExecutionResults({});
    setUserInputs({});
  }, [codeBlocks]);

  // Clear execution results when session changes (new session started)
  useEffect(() => {
    console.log('[CodeDisplay] Session ID changed, clearing all execution results and generated files');
    setExecutionResults({});
    setUserInputs({});
    setExecutingIndex(null);
    setShowInputModal(null);
  }, [sessionId]);

  // Supported languages for execution
  const executableLanguages = ['python', 'javascript', 'js', 'java', 'cpp', 'c', 'py', 'go', 'golang', 'rust'];

  // Check if code block is example/output (not executable code)
  const isExampleOutput = (code: string): boolean => {
    // Check for common example patterns
    const examplePatterns = [
      /===.*===/,  // === Results === or === Example ===
      /^(Enter|Input|Output|Result|Example|Sample):/im,  // Starts with prompt-like text
      /First.*list.*Second.*list/i,  // Example data descriptions
      /Merged.*list/i,
      /(Custom|Example|Sample|Demo).*Example/i,  // "Custom List Example", "Sample Example", etc.
      /^#+\s*(Example|Usage|Sample|Demo)/im,  // Markdown headings like "## Example"
      /\bUsage\s*:/i,  // "Usage:" sections
      /\bExample\s*\d+/i,  // "Example 1", "Example 2", etc.
      /^\s*#.*Example/im,  // Comments with "Example"
    ];

    const hasExamplePattern = examplePatterns.some(pattern => pattern.test(code));

    // Check if code is mostly print/output statements (likely just showing example output)
    const lines = code.split('\n').filter(line => line.trim().length > 0);
    const printLines = lines.filter(line =>
      /print\s*\(/.test(line) ||
      /console\.(log|info)/.test(line) ||
      /System\.out\.print/.test(line) ||
      /fmt\.Print/.test(line) ||
      /cout\s*<</.test(line)
    );
    const isPrintHeavy = printLines.length > 0 && printLines.length >= lines.length * 0.7;  // 70%+ print statements

    // If it has example patterns and doesn't have actual function definitions or loops, it's an example
    // Multi-language executable code detection (6 supported languages)
    const hasExecutableCode = (
      // Python: def, class, if/elif/else with colons, for/while with colons, input()
      /\bdef\s+\w+|\bclass\s+\w+|if\s+.*:|elif\s+.*:|else\s*:|for\s+.*:|while\s+.*:|input\s*\(/.test(code) ||

      // JavaScript/Node.js: function, if/else with braces, for/while with parens, const/let/var, =>
      /\bfunction\s+\w+|if\s*\(|else\s*\{|for\s*\(|while\s*\(|\bconst\s+\w+\s*=|\blet\s+\w+\s*=|\bvar\s+\w+\s*=|=>\s*\{/.test(code) ||

      // Java: public/private/protected, void/int/String, if/else with braces, Scanner, main method
      /\b(public|private|protected|void|int|String|double|float|boolean)\s+\w+|if\s*\(|else\s*\{|Scanner\s+\w+|static\s+void\s+main/.test(code) ||

      // C/C++: int main(), if/else with braces, scanf, cin/cout, #include
      /int\s+main\s*\(|void\s+main\s*\(|if\s*\(|else\s*\{|scanf\s*\(|cin\s*>>|cout\s*<<|#include\s*</.test(code) ||

      // Go: func main(), func name(), if/else with braces, fmt.Scan, package main
      /func\s+main\s*\(|func\s+\w+\s*\(|if\s+.*\{|else\s*\{|fmt\.Scan|package\s+main/.test(code) ||

      // Rust: fn main(), fn name(), let/mut, use, println!, match, struct, enum, impl
      /fn\s+main\s*\(|fn\s+\w+\s*\(|let\s+(mut\s+)?\w+|use\s+std::|println!\s*\(|match\s+\w+|struct\s+\w+|enum\s+\w+|impl\s+\w+/.test(code)
    );

    // If it's mostly print statements with hardcoded values, it's likely example output
    const hasHardcodedData = /\[\s*\d+\s*,\s*\d+/.test(code);  // Hardcoded lists like [1, 2, 3]

    return (hasExamplePattern && !hasExecutableCode) || (isPrintHeavy && hasHardcodedData);
  };

  // Check if code needs input
  const needsInput = (code: string, language?: string) => {
    // Python input detection
    const hasPythonInput = code.includes('input(') || code.includes('raw_input(');

    // Java Scanner detection (Scanner with System.in)
    const hasJavaScanner = (
      code.includes('Scanner') &&
      (code.includes('System.in') ||
       code.includes('.nextLine()') ||
       code.includes('.nextInt()') ||
       code.includes('.next()') ||
       code.includes('.nextDouble()') ||
       code.includes('.nextFloat()') ||
       code.includes('.nextLong()') ||
       code.includes('.nextBoolean()'))
    );

    // Java command-line arguments detection (args[0], args[1], etc.)
    const hasJavaArgs = (
      code.includes('args[0]') ||
      code.includes('args[1]') ||
      (code.includes('args.length') && code.includes('args[')) ||
      (code.includes('parseInt(args[') || code.includes('Integer.parseInt(args['))
    );

    // Combined Java input detection
    const hasJavaInput = hasJavaScanner || hasJavaArgs;

    // Node.js/JavaScript input detection
    const hasNodeInput = (
      (code.includes('readline') && code.includes('createInterface')) ||
      code.includes('process.stdin') ||
      code.includes('rl.question') ||
      code.includes('stdin.on') ||
      // Check for process.argv usage (command-line arguments)
      (code.includes('process.argv[2]') || code.includes('process.argv[3]')) ||
      (code.includes('process.argv') && code.includes('parseInt'))
    );

    // C input detection (scanf, getchar, fgets, etc.)
    const hasCInput = (
      code.includes('scanf(') ||
      code.includes('fscanf(') ||
      code.includes('getchar(') ||
      code.includes('gets(') ||
      code.includes('fgets(')
    );

    // C++ input detection (cin, getline, etc.)
    const hasCppInput = (
      (code.includes('cin') && code.includes('>>')) ||
      (code.includes('std::cin') && code.includes('>>')) ||
      code.includes('getline(') ||
      code.includes('std::getline(')
    );

    // Go input detection (fmt.Scan, bufio.Reader, etc.)
    const hasGoInput = (
      code.includes('fmt.Scan') ||
      code.includes('fmt.Scanf') ||
      code.includes('fmt.Scanln') ||
      code.includes('bufio.NewReader') ||
      code.includes('reader.ReadString') ||
      code.includes('bufio.NewScanner')
    );

    // Rust input detection (std::io::stdin, read_line)
    const hasRustInput = (
      code.includes('std::io::stdin') ||
      code.includes('io::stdin') ||
      code.includes('read_line')
    );

    return hasPythonInput || hasJavaInput || hasNodeInput || hasCInput || hasCppInput || hasGoInput || hasRustInput;
  };

  // Check if code has infinite loop with input
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const hasInfiniteLoop = (code: string) => {
    return (code.includes('while True:') || code.includes('while 1:')) && needsInput(code);
  };

  // Extract input prompts from code
  const extractInputPrompts = (code: string, language: string): string[] => {
    const prompts: string[] = [];

    if (language.toLowerCase() === 'python' || language.toLowerCase() === 'py') {
      // Match Python input() calls with string literals
      // Pattern: input("prompt") or input('prompt')
      const literalInputRegex = /input\s*\(\s*["']([^"']+)["']\s*\)/g;
      let match;

      // Collect ALL unique input() prompts from literal strings
      const seenPrompts = new Set<string>();
      while ((match = literalInputRegex.exec(code)) !== null) {
        seenPrompts.add(match[1]);
      }

      // Also look for function calls with string arguments (for functions that wrap input())
      // Pattern: function_name("prompt string")
      const functionCallRegex = /\b(get_\w+|input_\w+)\s*\(\s*["']([^"']+)["']\s*\)/g;
      while ((match = functionCallRegex.exec(code)) !== null) {
        seenPrompts.add(match[2]);
      }

      // Count total input() calls (including those with variables)
      const allInputRegex = /\binput\s*\(/g;
      const totalInputCalls = (code.match(allInputRegex) || []).length;

      console.log('[CodeDisplay] Total input() calls in code:', totalInputCalls);
      console.log('[CodeDisplay] Collected prompts:', Array.from(seenPrompts));

      // Use collected prompts
      prompts.push(...Array.from(seenPrompts));

      // If we didn't find enough prompts but have input() calls, add generic ones
      if (prompts.length < totalInputCalls && totalInputCalls <= 5) {
        const remaining = totalInputCalls - prompts.length;
        for (let i = 0; i < remaining; i++) {
          prompts.push(`Input ${prompts.length + i + 1}:`);
        }
      }

      console.log('[CodeDisplay] Final prompts to show:', prompts);
    } else if (language.toLowerCase() === 'javascript' || language.toLowerCase() === 'js') {
      // JavaScript with process.argv - detect command-line arguments
      if (code.includes('process.argv[2]') || code.includes('process.argv')) {
        // Count how many process.argv[N] references exist
        const argMatches = code.match(/process\.argv\[\d+\]/g);
        const uniqueArgs = new Set(argMatches || []);

        // Provide generic prompts for each argument
        if (uniqueArgs.size > 0) {
          // Start from argv[2] (argv[0] is node, argv[1] is script name)
          for (let i = 2; i < 2 + uniqueArgs.size; i++) {
            prompts.push(`Command-line argument ${i - 1}:`);
          }
        } else {
          // Default: assume one input
          prompts.push('Enter value:');
        }
      }
    } else if (language.toLowerCase() === 'java') {
      // Match Java System.out.print/println before Scanner operations
      const lines = code.split('\n');
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        // Check if this line has a print statement
        if (line.includes('System.out.print') && !line.includes('println(')) {
          const printMatch = line.match(/System\.out\.print\s*\(\s*"([^"]+)"\s*\)/);
          if (printMatch) {
            // Check if next few lines have Scanner input
            const nextLines = lines.slice(i + 1, i + 3).join('\n');
            if (nextLines.includes('next') || nextLines.includes('Scanner')) {
              prompts.push(printMatch[1]);
            }
          }
        }
      }
    } else if (language.toLowerCase() === 'go' || language.toLowerCase() === 'golang') {
      // Match Go fmt.Print before fmt.Scan
      const lines = code.split('\n');
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.includes('fmt.Print') && !line.includes('fmt.Println(')) {
          const printMatch = line.match(/fmt\.Print[f]?\s*\(\s*"([^"]+)"\s*\)/);
          if (printMatch) {
            const nextLines = lines.slice(i + 1, i + 3).join('\n');
            if (nextLines.includes('Scan')) {
              prompts.push(printMatch[1]);
            }
          }
        }
      }
    } else if (language.toLowerCase() === 'rust') {
      // Match Rust print!/println! before stdin read_line
      const lines = code.split('\n');
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.includes('print!') && !line.includes('println!')) {
          const printMatch = line.match(/print!\s*\(\s*"([^"]+)"/);
          if (printMatch) {
            const nextLines = lines.slice(i + 1, i + 3).join('\n');
            if (nextLines.includes('read_line') || nextLines.includes('stdin')) {
              prompts.push(printMatch[1]);
            }
          }
        }
      }
    } else if (language.toLowerCase() === 'cpp' || language.toLowerCase() === 'c') {
      // Match C/C++ cout << "prompt" or printf("prompt")
      const lines = code.split('\n');
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        // C++ cout
        if (line.includes('cout') && line.includes('<<')) {
          const coutMatch = line.match(/cout\s*<<\s*"([^"]+)"/);
          if (coutMatch) {
            const nextLines = lines.slice(i + 1, i + 3).join('\n');
            if (nextLines.includes('cin') || nextLines.includes('scanf')) {
              prompts.push(coutMatch[1]);
            }
          }
        }
        // C printf
        if (line.includes('printf')) {
          const printfMatch = line.match(/printf\s*\(\s*"([^"]+)"/);
          if (printfMatch) {
            const nextLines = lines.slice(i + 1, i + 3).join('\n');
            if (nextLines.includes('scanf') || nextLines.includes('getchar')) {
              prompts.push(printfMatch[1]);
            }
          }
        }
      }
    }

    return prompts;
  };

  const copyToClipboard = async (code: string, index: number) => {
    try {
      // Try clipboard API, fall back to textarea if it fails (e.g. "Document not focused")
      try {
        await navigator.clipboard.writeText(code);
      } catch {
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);

      // Track analytics (fire-and-forget)
      if (currentMessageId) {
        analyticsApi.trackCodeCopy({
          message_id: currentMessageId,
          code_block_index: index,
          was_copied: true
        }).catch(() => {});
      }
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const handleRunClick = (code: string, language: string, index: number) => {
    // Scroll to this code block
    if (codeBlockRefs.current[index]) {
      codeBlockRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }

    // If code needs input, extract prompts and show input section
    if (needsInput(code)) {
      const prompts = extractInputPrompts(code, language);
      if (prompts.length > 0) {
        // Multiple labeled inputs
        setMultipleInputs(new Array(prompts.length).fill(''));
        setShowInputModal(index);
      } else {
        // Fallback to single textarea
        setModalInput('');
        setMultipleInputs([]);
        setShowInputModal(index);
      }
    } else {
      // No input needed, execute directly
      executeCode(code, language, index, '');
    }
  };

  const handleModalSubmit = (index: number) => {
    const code = filteredCodeBlocks[index].code;
    const language = filteredCodeBlocks[index].language;

    console.log('[CodeDisplay] handleModalSubmit called');

    let finalInput = '';

    // If multiple labeled inputs, join them
    if (multipleInputs.length > 0 && multipleInputs.some(inp => inp.length > 0)) {
      // For Python, use comma-separated values
      if (language.toLowerCase() === 'python' || language.toLowerCase() === 'py') {
        finalInput = multipleInputs.join(',');
      } else {
        // For other languages, use newline-separated
        finalInput = multipleInputs.join('\n');
      }
    } else {
      // Fallback to single textarea input
      finalInput = modalInput;
    }

    console.log('[CodeDisplay] finalInput:', finalInput);

    // Store the input
    setUserInputs(prev => ({ ...prev, [index]: finalInput }));

    // Close modal
    setShowInputModal(null);
    setMultipleInputs([]);

    // Execute with provided input
    executeCode(code, language, index, finalInput);
  };

  const executeCode = async (code: string, language: string, index: number, input: string) => {
    console.log('[CodeDisplay] executeCode called');
    console.log('[CodeDisplay] input parameter:', input);
    console.log('[CodeDisplay] input parameter length:', input.length);

    if (!executableLanguages.includes(language.toLowerCase())) {
      setExecutionResults(prev => ({
        ...prev,
        [index]: {
          success: false,
          error: `Execution not supported for ${language}. Supported languages: ${executableLanguages.join(', ')}`,
          language
        }
      }));
      return;
    }

    // Clear previous results immediately when Run is clicked
    clearExecution(index);
    setExecutingIndex(index);

    try {
      console.log('[CodeDisplay] Sending to API with input:', input);
      console.log('[CodeDisplay] Session ID:', sessionId);
      const result = await analyticsApi.executeCode({
        code,
        language: language.toLowerCase(),
        input: input,
        session_id: sessionId
      });

      trackCodeExecuted(language.toLowerCase(), result.success);

      setExecutionResults(prev => ({
        ...prev,
        [index]: result
      }));

      // Auto-scroll to execution results after they're rendered
      setTimeout(() => {
        if (executionResultRefs.current[index]) {
          executionResultRefs.current[index]?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        }
      }, 100);

    } catch (error: any) {
      console.error('Execution failed:', error);
      setExecutionResults(prev => ({
        ...prev,
        [index]: {
          success: false,
          error: error.message || 'Execution request failed',
          language
        }
      }));
    } finally {
      setExecutingIndex(null);
    }
  };

  const clearExecution = (index: number) => {
    setExecutionResults(prev => {
      const newResults = { ...prev };
      delete newResults[index];
      return newResults;
    });
  };

  const isExecutable = (language: string) => {
    return executableLanguages.includes(language.toLowerCase());
  };

  if (filteredCodeBlocks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="space-y-3">
          <h4 className="text-lg font-semibold text-foreground">No code blocks yet</h4>
          <p className="text-sm text-muted-foreground max-w-sm">
            When Cubemate provides code examples, they'll appear here for copying and execution
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {filteredCodeBlocks.map((block, index) => (
        <div
          key={index}
          ref={(el) => { codeBlockRefs.current[index] = el; }}
          className="bg-card border border-border rounded-lg overflow-hidden shadow-lg"
        >
          {/* Header Section */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-gradient-to-r from-muted/50 to-muted/30">
            <div className="flex items-center space-x-3">
              <span className="px-2.5 py-1 bg-primary/10 text-primary text-xs font-mono font-bold uppercase rounded-md border border-primary/20">{block.language}</span>
              {isStreaming && (
                <div className="flex items-center space-x-1">
                  <div className="flex space-x-1">
                    <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce"></div>
                    <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                  <span className="text-xs text-blue-600 animate-pulse">Generating...</span>
                </div>
              )}
              {block.filename && (
                <span className="text-xs text-muted-foreground font-mono">{block.filename}</span>
              )}
              {/* Show "Example" badge if it's example output, otherwise show "Executable" if language is supported */}
              {isExampleOutput(block.code) ? (
                <span className="px-2.5 py-1 text-xs bg-blue-500/10 text-blue-600 rounded-full font-semibold border border-blue-500/20">
                  📘 Example
                </span>
              ) : isExecutable(block.language) && (
                <span className="px-2.5 py-1 text-xs bg-success/10 text-success rounded-full font-semibold border border-success/20">
                  ▶️ Executable
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => copyToClipboard(block.code, index)}
                className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all shadow-sm ${
                  copiedIndex === index
                    ? 'bg-success text-success-foreground scale-105'
                    : 'bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:scale-105'
                }`}
              >
                {copiedIndex === index ? '✓ Copied!' : '📋 Copy'}
              </button>

              {/* Only show Run button if it's NOT an example AND language is executable */}
              {!isExampleOutput(block.code) && isExecutable(block.language) && (
                <button
                  onClick={() => handleRunClick(block.code, block.language, index)}
                  className={`px-4 py-2 text-xs font-semibold rounded-lg transition-all shadow-sm ${
                    executingIndex === index
                      ? 'bg-muted text-muted-foreground cursor-not-allowed'
                      : 'bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105'
                  }`}
                  disabled={executingIndex === index}
                >
                  {executingIndex === index
                    ? '⏳ Running...'
                    : needsInput(block.code)
                      ? '▶️ Run with Input'
                      : '▶️ Run'
                  }
                </button>
              )}

              {executionResults[index] && (
                <button
                  onClick={() => clearExecution(index)}
                  className="px-4 py-2 text-xs font-semibold bg-destructive/10 text-destructive hover:bg-destructive/20 rounded-lg transition-all shadow-sm hover:scale-105"
                  title="Clear execution results"
                >
                  🗑️ Clear
                </button>
              )}
            </div>
          </div>

          {/* Code Section with Syntax Highlighting */}
          <div className="bg-gradient-to-br from-background to-muted/20 overflow-x-auto">
            <SyntaxHighlighter
              language={block.language.toLowerCase()}
              style={oneDark}
              customStyle={{
                margin: 0,
                padding: window.innerWidth < 768 ? '0.75rem' : '1.25rem',
                fontSize: window.innerWidth < 768 ? '0.8125rem' : '1.0625rem',
                fontWeight: '500',
                lineHeight: '1.4',
                borderRadius: 0,
                background: '#282c34'
              }}
              showLineNumbers={window.innerWidth >= 768}
              wrapLines={true}
              wrapLongLines={true}
            >
              {block.code}
            </SyntaxHighlighter>
          </div>

          {/* Execution Results Section */}
          {executionResults[index] && (
            <div
              ref={(el) => { executionResultRefs.current[index] = el; }}
              className="border-t-2 border-border bg-gradient-to-b from-muted/20 to-muted/40"
            >
              <div className="flex items-center justify-between px-5 py-3 bg-muted/50">
                <div className="flex items-center space-x-2">
                  <span className={`text-sm font-bold ${
                    executionResults[index].success ? 'text-success' : 'text-destructive'
                  }`}>
                    {executionResults[index].success ? '✅ Execution Results' : '❌ Execution Failed'}
                  </span>
                </div>
                {executionResults[index].success && executionResults[index].execution_time && (
                  <div className="flex items-center space-x-3 text-xs font-medium text-muted-foreground bg-background/50 px-3 py-1.5 rounded-full">
                    <span className="flex items-center space-x-1">
                      <span>⏱️</span>
                      <span>{executionResults[index].execution_time}s</span>
                    </span>
                    <span className="text-border">|</span>
                    <span className="flex items-center space-x-1">
                      <span>💾</span>
                      <span>{executionResults[index].memory_used}KB</span>
                    </span>
                  </div>
                )}
              </div>

              {executionResults[index].success ? (
                <div className="px-5 pb-5 space-y-4">
                  {/* Inline Image Preview */}
                  {executionResults[index].generated_files && executionResults[index].generated_files!.some(f => f.image_base64) && (
                    <div className="space-y-3">
                      {executionResults[index].generated_files!.filter(f => f.image_base64).map((file, imgIdx) => (
                        <div key={imgIdx} className="border border-border rounded-lg overflow-hidden bg-[#1a1a2e] flex flex-col items-center">
                          <div className="p-4 flex items-center justify-center w-full">
                            <img
                              src={`data:${file.image_mime || 'image/png'};base64,${file.image_base64}`}
                              alt={file.filename}
                              className="max-w-full max-h-[300px] md:max-h-[500px] object-contain rounded"
                            />
                          </div>
                          <div className="px-3 py-1.5 bg-muted/50 text-xs text-muted-foreground flex justify-between items-center w-full">
                            <span>{file.filename}</span>
                            <span>{(file.size / 1024).toFixed(1)} KB</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Generated Files Download Section - MOVED TO TOP FOR VISIBILITY */}
                  {executionResults[index].generated_files && executionResults[index].generated_files!.length > 0 && (
                    <div>
                      <div className="flex items-center space-x-2 mb-3">
                        <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide">📥 Generated Files</span>
                        <div className="flex-1 h-px bg-border"></div>
                      </div>
                      <div className="space-y-2">
                        {executionResults[index].generated_files!.map((file, fileIdx) => (
                          <div
                            key={fileIdx}
                            className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg hover:shadow-md transition-all"
                          >
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <span className="text-2xl">
                                {file.filename.endsWith('.csv') ? '📊' :
                                 file.filename.endsWith('.xlsx') || file.filename.endsWith('.xls') ? '📗' :
                                 file.filename.endsWith('.json') ? '📋' :
                                 file.filename.endsWith('.txt') ? '📄' :
                                 file.filename.match(/\.(png|jpg|jpeg|svg|gif)$/) ? '🖼️' : '📁'}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-foreground truncate">{file.filename}</p>
                                <p className="text-xs text-muted-foreground">
                                  {(file.size / 1024).toFixed(2)} KB
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  const token = localStorage.getItem('access_token');
                                  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

                                  const downloadUrl = `${API_URL}${file.download_url}`;

                                  const response = await fetch(downloadUrl, {
                                    headers: {
                                      'Authorization': `Bearer ${token}`
                                    }
                                  });

                                  if (!response.ok) {
                                    throw new Error(`Download failed: ${response.statusText}`);
                                  }

                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = file.filename;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                } catch (err) {
                                  console.error('Download failed:', err);
                                  alert('Failed to download file. Please try again.');
                                }
                              }}
                              className="px-4 py-2 text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-all shadow-sm hover:scale-105 flex items-center space-x-2"
                            >
                              <span>⬇️</span>
                              <span>Download</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Output Section - MOVED AFTER GENERATED FILES */}
                  {executionResults[index].output && executionResults[index].output!.trim() ? (
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs font-bold text-foreground uppercase tracking-wide">📤 Output</span>
                        <div className="flex-1 h-px bg-border"></div>
                      </div>
                      <pre className="p-4 text-sm bg-background border-2 border-success/20 rounded-lg text-foreground overflow-x-auto shadow-sm font-mono">
                        {executionResults[index].output}
                      </pre>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs font-bold text-foreground uppercase tracking-wide">📤 Output</span>
                        <div className="flex-1 h-px bg-border"></div>
                      </div>
                      <pre className="p-4 text-sm bg-background border-2 border-border rounded-lg text-muted-foreground italic shadow-sm">
                        (No output produced)
                      </pre>
                    </div>
                  )}

                  {/* Warnings/Stderr Section */}
                  {executionResults[index].error && executionResults[index].error!.trim() && (
                    <div>
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs font-bold text-amber-600 uppercase tracking-wide">⚠️ Warnings/Stderr</span>
                        <div className="flex-1 h-px bg-border"></div>
                      </div>
                      <pre className="p-4 text-sm bg-amber-50 dark:bg-amber-950/20 border-2 border-amber-200 dark:border-amber-800 rounded-lg text-amber-800 dark:text-amber-200 overflow-x-auto shadow-sm font-mono">
                        {executionResults[index].error}
                      </pre>
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-5 pb-5">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xs font-bold text-destructive uppercase tracking-wide">❌ Error</span>
                    <div className="flex-1 h-px bg-border"></div>
                  </div>
                  <pre className="p-4 text-sm bg-destructive/5 border-2 border-destructive/30 rounded-lg text-destructive overflow-x-auto shadow-sm font-mono">
                    {executionResults[index].error}
                  </pre>
                </div>
              )}

              {executionResults[index].status && (
                <div className="px-4 pb-2">
                  <small className="text-xs text-muted-foreground">Status: {executionResults[index].status}</small>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Simple environment info */}
      <div className="bg-card border border-border rounded-lg p-3 mt-4">
        <p className="text-xs text-muted-foreground">
          <strong className="text-foreground">Supported languages:</strong> Python, JavaScript, Java, C++, C, Go, Rust
        </p>
      </div>

      {/* Input Modal */}
      {showInputModal !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowInputModal(null)}>
          <div className="bg-card border-2 border-border rounded-lg shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="bg-primary/10 border-b border-border px-5 py-3 flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">
                💬 Program Input Required
              </h3>
              <button
                onClick={() => setShowInputModal(null)}
                className="text-muted-foreground hover:text-foreground transition-colors"
                title="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-3">
              {(() => {
                const code = filteredCodeBlocks[showInputModal]?.code || '';
                const language = filteredCodeBlocks[showInputModal]?.language || '';
                const prompts = extractInputPrompts(code, language);

                if (prompts.length > 0) {
                  // Show multiple labeled input fields
                  return (
                    <>
                      <div className="space-y-3">
                        {prompts.map((prompt, idx) => (
                          <div key={idx}>
                            <label className="block text-sm font-medium text-foreground mb-1.5">
                              {prompt}
                            </label>
                            <input
                              type="text"
                              value={multipleInputs[idx] || ''}
                              onChange={(e) => {
                                const newInputs = [...multipleInputs];
                                newInputs[idx] = e.target.value;
                                setMultipleInputs(newInputs);
                              }}
                              placeholder={`Enter value for: ${prompt}`}
                              className="w-full px-3 py-2 text-sm bg-background border-2 border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-mono"
                              autoFocus={idx === 0}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3">
                        <p className="text-xs text-green-800 dark:text-green-200">
                          ✨ Input fields detected automatically from your code!
                        </p>
                      </div>
                    </>
                  );
                } else {
                  // Fallback to single textarea
                  return (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Enter input (one per line):
                        </label>
                        <textarea
                          value={modalInput}
                          onChange={(e) => setModalInput(e.target.value)}
                          placeholder={
                            language.toLowerCase() === 'python' || language.toLowerCase() === 'py'
                              ? "10"
                              : "10"
                          }
                          className="w-full px-3 py-2 text-sm bg-background border-2 border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none font-mono"
                          rows={4}
                          autoFocus
                        />
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <p className="text-xs text-blue-800 dark:text-blue-200">
                          {language.toLowerCase() === 'python' || language.toLowerCase() === 'py' ? (
                            <>💡 For Python: Use <strong>comma-separated</strong> values (e.g., <code className="px-1 py-0.5 bg-blue-100 dark:bg-blue-900 rounded">10</code>)</>
                          ) : language.toLowerCase() === 'javascript' || language.toLowerCase() === 'js' ? (
                            <>💡 For JavaScript: Values are passed as <strong>command-line arguments</strong> (process.argv)</>
                          ) : (
                            <>💡 Enter each value on a <strong>separate line</strong></>
                          )}
                        </p>
                      </div>
                    </>
                  );
                }
              })()}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-border px-5 py-3 flex items-center justify-end gap-2 bg-muted/20">
              <button
                onClick={() => setShowInputModal(null)}
                className="px-4 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground border border-border rounded-md hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleModalSubmit(showInputModal)}
                className="px-5 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors shadow-sm"
              >
                ▶️ Run Program
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CodeDisplay;